"use client";

import { useMemo } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { PaginationControlsProps } from "@/lib/types/pagination";
import { cn } from "@/lib/utils";
import { buttonVariants } from "../ui/button";

function getPageItems(totalPages: number, currentPage: number, siblingCount = 1) {
  const ELLIPSIS = "...";
  const totalNumbers = siblingCount * 2 + 5; // first, last, current, 2*siblings
  if (totalPages <= totalNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const leftSibling = Math.max(currentPage - siblingCount, 1);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages);

  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  const items: (number | string)[] = [];
  items.push(1);
  if (showLeftEllipsis) items.push(ELLIPSIS);

  for (let p = leftSibling; p <= rightSibling; p++) {
    if (p !== 1 && p !== totalPages) items.push(p);
  }

  if (showRightEllipsis) items.push(ELLIPSIS);
  if (totalPages > 1) items.push(totalPages);
  return items;
}

export default function PaginationControls({
  meta,
  siblingCount = 1,
  className,
  createPageHref: customCreatePageHref,
  onPageChange,
}: PaginationControlsProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const pageItems = useMemo(
    () => getPageItems(meta.totalPages, meta.currentPage, siblingCount),
    [meta.totalPages, meta.currentPage, siblingCount]
  );

  // Default URL-based href creator
  const defaultCreatePageHref = (targetPage: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", String(targetPage));
    return `${pathname}?${params.toString()}`;
  };

  const createPageHref = customCreatePageHref || defaultCreatePageHref;

  const handlePageClick = (page: number, e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onPageChange) {
      e.preventDefault();
      onPageChange(page);
    }
  };

  if (meta.totalPages <= 1) {
    return null;
  }

  return (
    <Pagination className={className}>
      <PaginationContent className='gap-3'>
        <PaginationItem className='mr-auto'>
          <PaginationPrevious
            href={meta.hasPreviousPage ? createPageHref(meta.currentPage - 1) : "#"}
            aria-disabled={!meta.hasPreviousPage}
            className={cn(
              !meta.hasPreviousPage ? "pointer-events-none opacity-50" : undefined,
              buttonVariants({
                variant: "outline",
              }),
              "rounded-lg"
            )}
            onClick={(e) => meta.hasPreviousPage && handlePageClick(meta.currentPage - 1, e)}
          />
        </PaginationItem>
        {pageItems.map((item, idx) => (
          <PaginationItem key={`${item}-${idx}`}>
            {typeof item === "number" ? (
              <PaginationLink
                href={createPageHref(item)}
                isActive={item === meta.currentPage}
                onClick={(e) => handlePageClick(item, e)}
              >
                {item}
              </PaginationLink>
            ) : (
              <PaginationEllipsis />
            )}
          </PaginationItem>
        ))}
        <PaginationItem className='ml-auto'>
          <PaginationNext
            href={meta.hasNextPage ? createPageHref(meta.currentPage + 1) : "#"}
            aria-disabled={!meta.hasNextPage}
            className={cn(
              !meta.hasNextPage ? "pointer-events-none opacity-50" : undefined,
              buttonVariants({
                variant: "outline",
              }),
              "rounded-lg"
            )}
            onClick={(e) => meta.hasNextPage && handlePageClick(meta.currentPage + 1, e)}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
